import type React from 'react';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Icon from '~/components2/Icon';
import store from '../store';
import { IntelligenceSectionTitle } from '.';

interface Props {
  title: IntelligenceSectionTitle;
  featureCount?: number;
  children?: React.ReactNode;
  subheader?: React.ReactNode;
  contentClassName?: string;
}

export default function IntelligenceSection({ title, featureCount, children, subheader, contentClassName }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const { activeSection } = useSnapshot(store);

  const toggle = () => {
    if (activeSection === title) {
      store.activeSection = IntelligenceSectionTitle.None;
    } else {
      store.activeSection = title;

      // Since NearbyProjects is OPEN by default, I am putting this here
      // Each "SectionExpanded" event should be tracked by a "click".
      // With Nearby Projects open by default, it would always register
      // an event if this were to be in the useEffect below.
      if (store.project) {
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceSectionExpanded, {
          project_id: store.project.id,
          organization_id: store.project.organization,
          section_name: title,
        });
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (activeSection === title) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [activeSection]);

  return (
    <div className="flex flex-col rounded">
      <div
        onClick={toggle}
        data-testid={`intelligence-section-toggle-${title}`}
        className="flex w-full cursor-pointer items-center justify-between rounded bg-grey-10 px-0.5 py-1 px-2 hover:bg-grey-20"
      >
        <div className="text-base font-medium">
          {title} {(featureCount || featureCount === 0) && <span className="text-sm">{`(${featureCount})`}</span>}
        </div>

        <div>
          <Icon icon={isOpen ? 'chevronUp' : 'chevronDown'} />
        </div>
      </div>

      {isOpen && (
        <div className="">
          {subheader && <div className="my-2 flex w-full items-center justify-start space-x-2">{subheader}</div>}
          <div className={`mt-2 flex flex-col space-y-2 ${contentClassName}`}>{children}</div>
        </div>
      )}
    </div>
  );
}
